<template>
  <span class="pega-break">
    <div class="boss-table-title pega-print-title">
      <div class="pega-ta-right">
        <span data-test="label-company-name">{{ entryCompanyName }}
          <span
            v-if="headerUserName"
            data-test="header-info"
          >
            <br><span
              v-if="isDuplicatedEntries"
              class="boss-status-label pega-status-label pega-status-label-top-0"
              data-test="label-duplicated-entries"
            >重複応募
            </span>
            <span
              v-if="isSimpleEntryForm"
              class="boss-status-label pega-status-label pega-status-label-top-0"
              data-test="label-entry-form"
            >自己PR不要
            </span>
            応募者：{{ headerUserName }}様
          </span>
          <br><span data-test="label-resume-updated-at">
            {{ resumeUpdatedAt | parseDate }}現在
          </span>
        </span>
      </div>
    </div>
    <div class="boss-layout boss-layout-gap-between">
      <div>
        <div class="boss-resume-sheet-title">職務経歴書</div>
      </div>
    </div>
    <div class="boss-layout-block">
      <table class="boss-table-4 boss-table-4-responsive pega-th-color-important pega-resume">
        <tr>
          <td
            class="boss-table-4-space boss-table-4-space-title"
            colspan="5"
          >経験職種</td>
        </tr>
        <tr>
          <th>直近の経験職種</th>
          <td>
            <span data-test="text-recent-occupation">
              {{ recentOccupation | parseText('','') }}
            </span>
          </td>
        </tr>
        <tr>
          <th>経験職種</th>
          <td>
            <hidden-notice
              v-if="isSimpleEntryForm"
              data-test="text-experienced-occupations-simple-form"
            />
            <span
              v-else
              data-test="text-experienced-occupations"
              v-html="$options.filters.parseText(experiencedOccupationsText)"
            />
          </td>
        </tr>
      </table>
      <table class="boss-table-4 boss-table-4-responsive pega-th-color-important pega-resume">
        <tr>
          <td
            class="boss-table-4-space boss-table-4-space-title"
            colspan="5"
          >職務経歴</td>
        </tr>
        <template v-for="(career, i) in careers">
          <tr :key="`outline-overview-${i}`">
            <th
              :data-test="`head-career-${i}`"
              rowspan="4"
            >
              {{ workingPeriod(career) }}
            </th>
            <td
              :data-test="`text-career-outline-${i}`"
              colspan="2"
              class="boss-table-4-cell-group-top"
            >{{ career.companyName }} {{ career.businessCategory | parseText('業種','') }}（従業員数{{ career.employees | parseText('', '名') }}） {{ career.employmentStatus | parseText('雇用形態') }}{{ career | parseDispatchCompanyName }} 年収{{ career.salary | parseText('','万円') }}</td>
          </tr>
          <tr :key="`outline-business-${i}`">
            <th
              class="sub"
              :data-test="`head-career-business-content-${i}`"
            >
              事業内容
              <span
                v-tooltip="{ content: '事業内容の説明、所属部署、支店の説明など'}"
                class="pega-circle"
              >?</span>
            </th>
            <td
              class="pega-pre-wrap"
              :data-test="`text-career-business-content-${i}`"
              v-text="$options.filters.parseText(career.descriptionTxt1)"
            />
          </tr>
          <tr :key="`outline-job-${i}`">
            <th
              class="sub"
              :data-test="`head-career-job-content-${i}`"
            >
              仕事内容
              <span
                v-tooltip="{ content: '具体的な仕事内容、役割など'}"
                class="pega-circle"
              >?</span>
            </th>
            <td
              class="pega-pre-wrap"
              :data-test="`text-career-job-content-${i}`"
              v-text="$options.filters.parseText(career.descriptionTxt2)"
            />
          </tr>
          <tr :key="`outline-skill-${i}`">
            <th
              class="sub"
              :data-test="`head-career-skill-content-${i}`"
            >成果・経験</th>
            <td
              class="pega-pre-wrap"
              :data-test="`text-career-skill-content-${i}`"
              v-text="$options.filters.parseText(career.descriptionTxt3)"
            />
          </tr>
        </template>
      </table>
      <table class="boss-table-4 boss-table-4-responsive pega-th-color-important pega-resume">
        <tr>
          <td
            class="boss-table-4-space boss-table-4-space-title"
            colspan="5"
          >スキル・経験</td>
        </tr>
        <tr
          v-for="master in skillsMaster"
          :key="master.id"
        >
          <th :data-test="`head-skills-${master.id}`">
            {{ master.name }}
          </th>
          <td :data-test="`detail-skills-${master.id}`">
            <hidden-notice v-if="isSimpleEntryForm" />
            <template v-else>
              <template v-if="layeredSkillsByLv2Category[master.id].length">
                <template v-for="(skillL2CategoryAndSkillDetails , i) in layeredSkillsByLv2Category[master.id]">
                  <p
                    v-if="hasChildCategorySkillBy(master.id)"
                    :key="`${master.id}-${i}`"
                    :data-test="`label-skills-category-${master.id}-${i}`"
                    class="boss-text"
                  >[{{ skillL2CategoryAndSkillDetails[0] }}]</p>
                  <dl
                    :key="`dl-${master.id}-${i}`"
                    class="boss-definition-list"
                  >
                    <dd
                      v-for="(detail, j) in skillL2CategoryAndSkillDetails[1]"
                      :key="`${master.id}-${i}-${j}`"
                      :data-test="`text-skills-${master.id}-${i}-${j}`"
                    >
                      {{ detail | parseSkillDetail }}
                    </dd>
                  </dl>
                </template>
              </template>
              <template v-else>
                未登録
              </template>
            </template>
          </td>
        </tr>
      </table>
    </div>
  </span>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { parseHierarchicalObjects, skills, layeredSkillsByLv2CategoryMap } from '@/lib/parse'
import hiddenNotice from '@/components/atoms/HiddenNotice'
import dayjs from 'dayjs'

export default {
  components: {
    hiddenNotice
  },
  filters: {
    parseText (val, prefix = '', suffix = '') {
      return (val) ? val + suffix : `${prefix}未登録`
    },
    parseSkillDetail (val) {
      return `${val.name}（${val.experiencedName}）`
    },
    parseDate (val) {
      return (val) ? dayjs(val).format('YYYY年MM月DD日') : ''
    },
    parseDispatchCompanyName (career) {
      if (career.employmentStatus === '紹介予定派遣・派遣') {
        return `（派遣元企業名：${career.dispatchCompanyName || '未登録'}）`
      } else {
        return ''
      }
    }
  },
  props: {
    isDuplicatedEntries: {
      type: Boolean,
      default: false
    },
    isSimpleEntryForm: {
      type: Boolean,
      default: false
    },
    headerUserName: {
      type: String,
      default: ''
    },
    entryId: {
      type: Number,
      required: true
    },
    entryCompanyName: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      experiencedOccupations: [],
      careers: [],
      skills: [],
      resumeUpdatedAt: '',
      skillNote: '',
      recentOccupation: ''
    }
  },
  computed: {
    experiencedOccupationsText () {
      return _(this.experiencedOccupations).join('<br>')
    },
    workingPeriod () {
      return (work) => {
        return `${this.toLocalDate(work.firstYear, work.firstMonth)} ～ ${this.toLocalDate(work.lastYear, work.lastMonth)}`
      }
    },
    ...mapGetters([
      'skillsMaster',
      'hasChildCategorySkillBy'
    ]),
    layeredSkillsByLv2Category () {
      return layeredSkillsByLv2CategoryMap(this.skillsMaster, this.skills, 'name')
    },
    parsedSkillsMaster () {
      return parseHierarchicalObjects(this.skillsMaster)
    }
  },
  watch: {
    entryId: {
      handler () {
        this.fetchAll()
      }
    },
    skillsMaster: {
      handler () {
        // skillsがskillsMasterに依存しているため、skillsMasterの変更毎にfetchSkillsを再実行する
        // ケース例: マスタ取得が間に合っていない場合にskillsが空になってしまう
        this.fetchSkills()
      }
    }
  },
  created () {
    this.fetchAll()
  },
  methods: {
    toLocalDate (year, month) {
      // 職務経歴書で9999が登録されている場合は現在就業中
      if (year === 9999) return '現在就業中'
      // 9999以外の日付が入力されている場合は登録されている値
      if (year && month) return `${year}年${month}月`
      return '未登録' // それ以外は未登録と扱う
    },
    fetchExperiencedOccupations () {
      this.$http.secured.get(`/entry/view/entries/${this.entryId}/resume/experienced_occupations`).then(response => {
        this.$set(this, 'experiencedOccupations', response.data)
      })
    },
    fetchCareer () {
      this.$http.secured.get(`/entry/view/entries/${this.entryId}/resume/career`).then(response => {
        this.$set(this, 'careers', response.data)
      })
    },
    fetchSkills () {
      this.$http.secured.get(`/entry/view/entries/${this.entryId}/resume/skills`).then(response => {
        this.$set(this, 'skills', skills(response.data, this.parsedSkillsMaster))
      })
    },
    fetchResume () {
      this.$http.secured.get(`/entry/view/entries/${this.entryId}/resume`).then(response => {
        this.$set(this, 'resumeUpdatedAt', _.get(response.data, 'updatedDate', ''))
        this.$set(this, 'recentOccupation', _.get(response.data, 'recentJob', ''))
      })
    },
    fetchAll () {
      this.fetchExperiencedOccupations()
      this.fetchCareer()
      this.fetchSkills()
      this.fetchResume()
    }
  }
}
</script>
